import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trophy, Coins, Lightning, Users, Plus } from "@phosphor-icons/react";

export default function Challenges() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [awardOpen, setAwardOpen] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", reward_pool_credits: 1000, deadline: "", category: "" });

  const load = async () => {
    const { data } = await api.get("/challenges");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const join = async (id) => {
    try { await api.post(`/challenges/${id}/join`); toast.success("Joined"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const create = async () => {
    try {
      await api.post("/challenges", { ...form, deadline: form.deadline || null });
      toast.success("Challenge created");
      setShowCreate(false);
      setForm({ title: "", description: "", reward_pool_credits: 1000, deadline: "", category: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const isAdmin = user.role === "ADMIN";

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="challenges-page">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Trophy size={26} weight="fill" className="text-[#E1252A]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Innovation</div>
            <h1 className="text-3xl sm:text-4xl tracking-tight font-bold">Challenges</h1>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreate(true)} className="rounded-sm bg-foreground text-background" data-testid="create-challenge-btn">
            <Plus size={14} className="mr-1" /> New challenge
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.challenge_id} className="border border-border bg-background p-5 space-y-3" data-testid={`challenge-${c.challenge_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                {c.category && <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{c.category}</div>}
                <h3 className="text-xl font-semibold tracking-tight leading-snug">{c.title}</h3>
              </div>
              <Badge variant="outline" className="rounded-sm text-[10px] uppercase font-mono">{c.status}</Badge>
            </div>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">{c.description}</p>
            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border text-xs">
              <div>
                <div className="text-zinc-500 flex items-center gap-1"><Coins size={11} className="text-[#E1252A]" /> Pool</div>
                <div className="text-lg font-bold font-mono text-[#E1252A]">{c.reward_pool_credits}</div>
              </div>
              <div>
                <div className="text-zinc-500 flex items-center gap-1"><Users size={11} /> Joined</div>
                <div className="text-lg font-bold font-mono">{(c.participant_ids || []).length}</div>
              </div>
              <div>
                <div className="text-zinc-500 flex items-center gap-1"><Lightning size={11} /> Deadline</div>
                <div className="text-xs font-mono">{c.deadline ? new Date(c.deadline).toLocaleDateString() : "—"}</div>
              </div>
            </div>
            {c.status === "awarded" && c.winners?.length > 0 && (
              <div className="pt-3 border-t border-border">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Winners</div>
                <div className="flex flex-wrap gap-2">
                  {c.winners.map((w) => (
                    <Badge key={w.user_id} className="rounded-sm bg-[#E1252A]/10 text-[#E1252A] border border-[#E1252A]/30" data-testid={`winner-${w.user_id}`}>
                      🏆 {w.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
              {c.status === "open" && !(c.participant_ids || []).includes(user.user_id) && (
                <Button size="sm" onClick={() => join(c.challenge_id)} className="rounded-sm bg-foreground text-background" data-testid={`join-${c.challenge_id}`}>Join</Button>
              )}
              {(c.participant_ids || []).includes(user.user_id) && c.status === "open" && (
                <Badge variant="outline" className="rounded-sm">You're in</Badge>
              )}
              {isAdmin && c.status === "open" && (
                <Button size="sm" variant="outline" onClick={() => setAwardOpen(c)} className="rounded-sm ml-auto" data-testid={`award-${c.challenge_id}`}>Award winners</Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center text-sm text-zinc-500 p-12 border border-dashed border-border" data-testid="challenges-empty">
            No challenges yet. {isAdmin && "Kick off innovation with your first challenge above."}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="create-challenge-dialog">
          <DialogHeader><DialogTitle>New Innovation Challenge</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="challenge-title-input" /></div>
            <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" data-testid="challenge-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Reward pool (credits)</Label><Input type="number" min={0} value={form.reward_pool_credits} onChange={(e) => setForm({ ...form, reward_pool_credits: Number(e.target.value) })} className="rounded-sm" data-testid="challenge-pool-input" /></div>
              <div><Label>Deadline</Label><Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="rounded-sm" data-testid="challenge-deadline-input" /></div>
            </div>
            <div><Label>Category (optional)</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Cost Optimization" className="rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button onClick={create} className="rounded-sm bg-foreground text-background" data-testid="challenge-submit-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Award dialog */}
      {awardOpen && <AwardDialog challenge={awardOpen} onClose={() => setAwardOpen(null)} onDone={() => { setAwardOpen(null); load(); }} />}
    </div>
  );
}

function AwardDialog({ challenge, onClose, onDone }) {
  const [winners, setWinners] = useState([]);
  const [notes, setNotes] = useState("");

  const addRow = () => setWinners([...winners, { user_id: (challenge.participants[winners.length] || {}).user_id || "", share_pct: 100 - winners.reduce((a, w) => a + w.share_pct, 0) }]);
  const update = (i, k, v) => setWinners(winners.map((w, idx) => idx === i ? { ...w, [k]: v } : w));
  const remove = (i) => setWinners(winners.filter((_, idx) => idx !== i));

  const total = winners.reduce((a, w) => a + Number(w.share_pct || 0), 0);

  const award = async () => {
    try {
      await api.post(`/challenges/${challenge.challenge_id}/award`, { winners, notes });
      toast.success("Winners awarded");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="award-dialog">
        <DialogHeader><DialogTitle>Award "{challenge.title}"</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-500">Pool: {challenge.reward_pool_credits} credits. Shares must sum to ≤100%.</div>
          {winners.map((w, i) => (
            <div key={i} className="grid grid-cols-6 gap-2 items-end">
              <select className="col-span-4 rounded-sm border border-border bg-background p-2 text-sm" value={w.user_id} onChange={(e) => update(i, "user_id", e.target.value)} data-testid={`winner-select-${i}`}>
                <option value="">Select participant…</option>
                {(challenge.participants || []).map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
              </select>
              <Input type="number" min={1} max={100} value={w.share_pct} onChange={(e) => update(i, "share_pct", Number(e.target.value))} className="rounded-sm" data-testid={`winner-share-${i}`} />
              <Button size="sm" variant="outline" onClick={() => remove(i)} className="rounded-sm">×</Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addRow} className="rounded-sm" data-testid="add-winner-row">+ Add winner</Button>
          <div className={`text-xs ${total <= 100 ? "text-zinc-500" : "text-red-600"}`}>Total share: {total}%</div>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="rounded-sm" />
        </div>
        <DialogFooter>
          <Button onClick={award} disabled={winners.length === 0 || total <= 0 || total > 100} className="rounded-sm bg-foreground text-background" data-testid="award-submit-btn">Award</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
